import { useState } from "react";
import Input from "../components/Input";
import { T } from "../constants/theme";
import Btn from "../components/Btn";
import Badge from "../components/Badge";
import StepBar from "../components/StepBar";
import { API_BASE_URL } from "../config";

export default function ReviewStep({ data, studentId, onNext, onBack }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const Row = ({ label, value }) => (
    <div
      style={{
        display: "flex",

        padding: "9px 0",

        borderBottom: `1px solid ${T.gray100}`,
      }}
    >
      <span
        style={{ width: 170, flexShrink: 0, fontSize: 13, color: T.gray600 }}
      >
        {label}
      </span>

      <span style={{ fontSize: 13, fontWeight: 600, color: T.gray800 }}>
        {value || "—"}
      </span>
    </div>
  );

  const features = [
    "State-Level Online Examination",
    "3 Full Mock Tests",
    "Monthly Practice Tests (July 2026 – March 2027)",
    "Student Dashboard",
    "Performance Analysis Report",
    "Hybrid Guidance Seminars",
    "Digital Certificate",
    "State, District & Taluka Rankings",
  ];

  const validate = () => {
    const e = {};

    if (!password) e.password = "Password is required";
    else if (password.length < 6)
      e.password = "Password must be at least 6 characters";

    if (password !== confirmPassword)
      e.confirmPassword = "Passwords do not match";

    setErrors(e);

    return Object.keys(e).length === 0;
  };


  const sendEmail = async (registrationId, password) => {
    const response = await fetch(
      `${API_BASE_URL}/api/Registration/${registrationId}/send-email`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          password,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to send email");
    }
  };

  const savePassword = async () => {
    if (!validate()) return;

    try {
      setLoading(true);

      const registrationId = sessionStorage.getItem("registrationId");

      if (!registrationId) {
        throw new Error("Registration ID not found. Please register again.");
      }

      // Save password
      const response = await fetch(
        `${API_BASE_URL}/api/Registration/${registrationId}/password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            password,
          }),
        }
      );

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || "Failed to save password");
      }

      // Send email with Student ID and Password
      await sendEmail(registrationId, password);

      sessionStorage.setItem("tempPassword", password);

      onNext({ password });
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleBack = () => {
    onBack();
  };

  return (
    <div>
      <StepBar step={2} />

      <div style={{ marginBottom: 22 }}>
        <Badge>Step 2 of 5</Badge>

        <h2
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: T.indigo,
            margin: "8px 0 4px",
          }}
        >
          Review & Confirm Registration
        </h2>

        <p style={{ fontSize: 13, color: T.gray600 }}>
          Please verify your information before proceeding to payment.
        </p>
      </div>

      {/* Student ID */}
      <div
        style={{
          background: `linear-gradient(135deg, ${T.indigo} 0%, ${T.indigoDark} 100%)`,
          borderRadius: 12,
          padding: "14px 18px",
          marginBottom: 18,
          color: T.white,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              opacity: 0.7,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Your Student ID
          </div>

          <div
            style={{
              fontSize: 22,
              fontWeight: 900,
              letterSpacing: "0.1em",
              marginTop: 2,
            }}
          >
            {sessionStorage.getItem("studentId")}
          </div>
        </div>

        <Badge color={T.saffron}>Auto-Generated</Badge>
      </div>

      {/* Profile Summary */}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "14px 16px",
          background: T.gray50,
          borderRadius: 10,
          marginBottom: 18,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 8,
            background: T.gray200,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {data.photoPreview ? (
            <img
              src={data.photoPreview}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 22,
              }}
            >
              👤
            </div>
          )}
        </div>

        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: T.indigo }}>
            {data.name}
          </div>

          <div style={{ fontSize: 13, color: T.gray600 }}>
            {data.grade} · {data.medium} Medium
          </div>

          <div style={{ fontSize: 12, color: T.gray400, marginTop: 2 }}>
            {data.school}
          </div>
        </div>
      </div>

      {/* Read-only details */}

      {/* Account Credentials */}

      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: T.gray600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginBottom: 12,
        }}
      >
        Account Credentials
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Input
          label="Create Password"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="Min. 6 characters"
          required
          error={errors.password}
        />

        <Input
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Re-enter password"
          required
          error={errors.confirmPassword}
        />
      </div>

      {/* Registration Benefits */}

      <div
        style={{
          background: T.gray50,
          borderRadius: 12,
          padding: "16px 18px",
          marginBottom: 22,
          marginTop: 6,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: T.gray600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            marginBottom: 12,
          }}
        >
          Your Registration Includes
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
        >
          {features.map((f, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                fontSize: 13,
                color: T.gray800,
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 1, color: T.green }}>
                ✅
              </span>

              <span>{f}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <Btn onClick={handleBack} variant="outline">
          ← Edit Details
        </Btn>

        <Btn onClick={savePassword} full disabled={loading}>
          {loading
            ? "Saving Password..."
            : "Proceed to Payment →"}
        </Btn>
      </div>
    </div>
  );
};